import { Menu } from './Consumable'

export class CoverMenuChoice {
    constructor(
        public name: string | null,
        public notes: string | null,
        public selections: CoverProductSelection[]
    ) {}
}

export class CoverProductSelection {
    constructor(
        public productSkuId: string,
        public modifierSkuIds: string[],
        public menuItemId: string | null = null,
    ) {}
}

export class BookingDocket {
    constructor(
        public id: string,
        public menuId: string,
        public coverChoices: CoverMenuChoice[],
        public dateCreated: Date,
        public dateUpdated: Date,
        public minimumCoursesRequired: number | null = null,
        public maximumCoursesRequired: number | null = null
    ) {}

    public getCourseLimits(menu: Menu) {
        return {
            minimumCoursesRequired: this.minimumCoursesRequired ?? menu.minimumCoursesRequired,
            maximumCoursesRequired: this.maximumCoursesRequired ?? menu.maximumCoursesRequired,
        }
    }

    public isComplete(partySize: number): boolean {
        if (partySize <= 0) {
            return true
        }
        if (!this.coverChoices || this.coverChoices.length < partySize) {
            return false
        }
        const withAtLeastOneChoice = this.coverChoices.filter(c => c.selections && c.selections.length > 0).length
        return withAtLeastOneChoice >= partySize
    }

    public completedCovers(partySize: number): number {
        const total = Math.max(partySize, 0)
        const withAtLeastOne = (this.coverChoices || []).filter(c => c.selections && c.selections.length > 0).length
        return Math.min(withAtLeastOne, total)
    }

    public progressPercent(partySize: number): number {
        const total = Math.max(partySize, 0)
        if (total === 0) { return 0 }
        return Math.round((this.completedCovers(total) / total) * 100)
    }

    public normalizeChoices(partySize: number): CoverMenuChoice[] {
        const target = Math.max(partySize, 0)
        const source = (this.coverChoices || [])
        const copy: CoverMenuChoice[] = source.slice(0, target).map(c => new CoverMenuChoice(
            c?.name ?? null,
            c?.notes ?? null,
            [...(c?.selections ?? [])]
        ))
        while (copy.length < target) {
            copy.push(new CoverMenuChoice(null, null, []))
        }
        return copy
    }

    public equalsNormalized(otherChoices: CoverMenuChoice[], partySize: number): boolean {
        const a = this.normalizeChoices(partySize)
        const b = BookingDocket.normalizeExternalChoices(otherChoices, partySize)
        if (a.length !== b.length) { return false }
        for (let i = 0; i < a.length; i++) {
            if ((a[i].name ?? null) !== (b[i].name ?? null)) { return false }
            if ((a[i].notes ?? null) !== (b[i].notes ?? null)) { return false }
            if (!BookingDocket.compareSelections(a[i].selections || [], b[i].selections || [])) { return false }
        }
        return true
    }

    private static normalizeExternalChoices(choices: CoverMenuChoice[] | null | undefined, partySize: number): CoverMenuChoice[] {
        const target = Math.max(partySize, 0)
        const source = (choices || [])
        const copy: CoverMenuChoice[] = source.slice(0, target).map(c => new CoverMenuChoice(
            c?.name ?? null,
            c?.notes ?? null,
            [...(c?.selections ?? [])]
        ))
        while (copy.length < target) {
            copy.push(new CoverMenuChoice(null, null, []))
        }
        return copy
    }

    private static compareSelections(a: CoverProductSelection[], b: CoverProductSelection[]): boolean {
        const normalize = (selections: CoverProductSelection[]) => selections.map(selection => JSON.stringify([
            selection.productSkuId, selection.menuItemId ?? null, [...selection.modifierSkuIds].sort(),
        ])).sort()
        const normalizedA = normalize(a)
        const normalizedB = normalize(b)
        return normalizedA.length === normalizedB.length && normalizedA.every((selection, index) => selection === normalizedB[index])
    }
}
